import * as faceapi from '@vladmandic/face-api';
import { getEmojiForEmotion } from '@/components/EmotionDetector';

// Map emotions to emoji files - same mapping but exposed for VideoPlayer
export const adjustedEmotionEmojiMap: Record<string, string> = {
  angry: "/emojis/angry.png",
  disgust: "/emojis/disgust.png",
  fear: "/emojis/Fear.png",
  happy: "/emojis/Happy.png",
  neutral: "/emojis/Neutral.png",
  sad: "/emojis/Sad.png",
  surprised: "/emojis/Surprise.png",
  ecstatic: "/emojis/Ecstatic.png",
  'just-ok': "/emojis/Just-Ok.png",
  'ohmygod': "/emojis/OhMyGod.png",
  'contempt': "/emojis/contempt.png",
  'bored': "/emojis/bored.png",
  'anxious': "/emojis/Anxious.png",
  'startled': "/emojis/Startled.png",
  'flat': "/emojis/Flat.png",
  // Ensure case-insensitive lookups work
  'Anxious': "/emojis/Anxious.png",
  'Startled': "/emojis/Startled.png",
  'Flat': "/emojis/Flat.png",
  'Ecstatic': "/emojis/Ecstatic.png",
  'Just-Ok': "/emojis/Just-Ok.png",
  'OhMyGod': "/emojis/OhMyGod.png",
  'Contempt': "/emojis/contempt.png",
  'Bored': "/emojis/bored.png",
  // Fallback for any other emotions
  default: "/emojis/Neutral.png"
};

// Helper function to get emoji for adjusted emotion from VideoPlayer
export const getEmojiForAdjustedEmotion = (emotion: string): string => {
  if (!emotion) return adjustedEmotionEmojiMap.default;
  
  // Normalize and find emoji for the emotion
  const normalizedEmotion = emotion.toLowerCase().trim();
  return adjustedEmotionEmojiMap[normalizedEmotion] || adjustedEmotionEmojiMap.default;
};

class EmotionDetectionService {
  private modelsLoaded: boolean = false;
  private modelLoadPromise: Promise<boolean> | null = null;

  constructor() {
    console.log('Emotion Detection Service initialized');
  }
  
  private formatValue(value: number): string {
    if (isNaN(value) || !isFinite(value)) return '0%';
    return `${Math.round(value * 100)}%`;
  }

  private mapExpressionToEmotion(expression: string): string {
    // Map face-api expressions to our emotion categories
    switch (expression) {
      case 'happy': return 'happy';
      case 'sad': return 'sad';
      case 'angry': return 'angry';
      case 'fearful': return 'fear';
      case 'disgusted': return 'disgust';
      case 'surprised': return 'surprised';
      case 'neutral': return 'neutral';
      default: return expression;
    }
  }

  public async loadModels(): Promise<boolean> {
    // If already loading, return the existing promise
    if (this.modelLoadPromise) {
      return this.modelLoadPromise;
    }

    // If already loaded, return success immediately
    if (this.modelsLoaded) {
      return Promise.resolve(true);
    }

    // Create a new loading promise
    this.modelLoadPromise = new Promise<boolean>(async (resolve) => {
      try {
        // Try to load models from standard path
        try {
          // Set the models path
          const modelsPath = '/models';
          console.log(`Loading face detection models from: ${modelsPath}`);
          
          // Load the face detection models
          await Promise.all([
            faceapi.nets.tinyFaceDetector.load(modelsPath),
            faceapi.nets.faceExpressionNet.load(modelsPath)
          ]);
          
          console.log('Face detection models loaded successfully');
          this.modelsLoaded = true;
          return resolve(true);
        } catch (error) {
          console.error('Error loading face detection models:', error);
          
          // Attempt to load from CDN as fallback
          console.log('Attempting to load models from CDN as fallback...');
          
          try {
            // Load from jsdelivr CDN as a fallback
            const cdnPath = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';
            await Promise.all([
              faceapi.nets.tinyFaceDetector.load(cdnPath),
              faceapi.nets.faceExpressionNet.load(cdnPath)
            ]);
            
            console.log('TinyFaceDetector model loaded from CDN');
            console.log('Face expression model loaded from CDN');
            this.modelsLoaded = true;
            return resolve(true);
          } catch (cdnError) {
            console.error('Error loading models from CDN:', cdnError);
            return resolve(false);
          }
        }
      } catch (finalError) {
        console.error('Fatal error loading face models:', finalError);
        return resolve(false);
      } finally {
        // Reset the promise regardless of outcome
        this.modelLoadPromise = null;
      }
    });

    return this.modelLoadPromise;
  }

  public async detectEmotion(videoElement: HTMLVideoElement) {
    if (!this.modelsLoaded) {
      console.warn('Face detection models not loaded yet');
      return null;
    }

    if (!videoElement || videoElement.readyState < 2) {
      console.warn('Video not ready for emotion detection');
      return null;
    }

    try {
      const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.5 });
      
      // Detect faces with expressions
      const result = await faceapi
        .detectSingleFace(videoElement, options)
        .withFaceExpressions();

      if (result && result.expressions) {
        // Find the emotion with highest confidence
        let highestValue = 0;
        let highestEmotion = 'neutral';

        Object.entries(result.expressions).forEach(([emotion, value]) => {
          if (value > highestValue) {
            highestValue = value;
            highestEmotion = emotion;
          }
        });

        // Map to our standardized emotion
        const adjustedEmotion = this.mapExpressionToEmotion(highestEmotion);
        
        // Get matching emoji for this emotion
        const emojiUrl = getEmojiForEmotion(adjustedEmotion);
        
        return {
          adjustedEmotion,
          emojiUrl,
          confidence: highestValue * 100, // Convert to percentage
          percentageText: this.formatValue(highestValue),
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error detecting emotion:', error);
      return null;
    }
  }

  // Separate method to pre-load models ahead of time
  public async preloadModels() {
    try {
      await this.loadModels();
      return this.modelsLoaded;
    } catch (error) {
      console.error('Error in preloadModels:', error);
      return false;
    }
  }
}

// Create and export a singleton instance
const emotionDetectionService = new EmotionDetectionService();
export default emotionDetectionService;
